import {Component, computed, inject, input, signal} from "@angular/core";
import {ExamSession, ExamStation, Participant, ParticipantType, SimUser} from "@models";
import {SimMaterialDialogService, SimScenarioService, SimTrainingRoomService, SimUserService} from "@services";
import {derivedAsync} from "ngxtension/derived-async";
import {MatChip, MatChipSet} from "@angular/material/chips";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {TranslateModule} from "@ngx-translate/core";
import {Translations} from "@translations";
import {GetTrainingRoomByIdPipe} from "@common/pipes/get-training-room-by-id.pipe";
import {AsyncPipe} from "@angular/common";

@Component({
	selector: "sim-exam-session-card-station-content",
	templateUrl: "./exam-session-card-station-content.component.html",
	styleUrl: "./exam-session-card-station-content.component.scss",
	imports: [MatChipSet, MatChip, MatIcon, MatProgressSpinner, TranslateModule, GetTrainingRoomByIdPipe, AsyncPipe],
})
export class ExamSessionCardStationContentComponent {
	// Inputs
	public station = input.required<ExamStation>();
	public index = input.required<number>();
	public examSession = input.required<ExamSession>();

	// Constants
	protected readonly T = Translations;

	// Properties
	public participants = derivedAsync(
		async () => {
			return this._computeParticipants(this.station());
		},
		{initialValue: []},
	);
	public isLoading = signal<boolean>(true);
	protected readonly examStation = computed<ExamStation | null>(() => {
		return this.station() as ExamStation;
	});
	protected readonly ParticipantType = ParticipantType;

	// Injections
	private _dialogService = inject(SimMaterialDialogService);
	private _roomService = inject(SimTrainingRoomService);
	private _scenarioService = inject(SimScenarioService);
	private _userService = inject(SimUserService);

	protected async openRoomDetailDialog(trainingRoomId: string) {
		const trainingRoom = await this._roomService.getTrainingRoomById(trainingRoomId);
		await this._dialogService.openShowRoomDetailDialog(
			{trainingRoom},
			{
				disableClose: false,
				width: "1200px",
			},
		);
	}

	protected openUserCardDialog(user: SimUser) {
		this._dialogService.openUserCardMatDialog({userData: user}).then();
	}

	protected async openScenarioInformationDialog(scenarioId: string) {
		const scenario = await this._scenarioService.getScenarioById(scenarioId);
		const trainingSession = this.examSession();
		await this._dialogService.openScenarioInformationDialog({scenario, trainingSession});
	}

	private async _computeParticipants(station: ExamStation): Promise<Partial<Participant>[]> {
		const participants: Partial<Participant>[] = [];
		const requests: Promise<unknown>[] = [];

		if (station.standardizedPatientUserId) {
			const fetchSp = this._userService.getUserById(station.standardizedPatientUserId).then((sp) => {
				participants.push({
					user: sp,
					participantType: ParticipantType.StandardizedPatient,
					userId: sp.id,
				});
			});
			requests.push(fetchSp);
		}
		if (station.examinerUserId) {
			const fetchExaminer = this._userService.getUserById(station.examinerUserId).then((examiner) => {
				participants.push({
					user: examiner,
					participantType: ParticipantType.Instructor,
					userId: examiner.id,
				});
			});
			requests.push(fetchExaminer);
		}
		if (requests.length === 0) {
			this.isLoading.set(false);
		}
		await Promise.all(requests);
		this.isLoading.set(false);
		return participants;
	}
}
